import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {AddEmployeeService} from 'src/app/services/add-employee.service';
import {CloseHandlerService} from 'src/app/services/close-handler.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ActivatedRoute, Router} from '@angular/router';
import {Utils} from 'src/app/shared/utils/utils';
import {FileTypes} from 'src/app/shared/utils/type-checker';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {
  EmploymentFormStatus,
  ISalaryRecruiter,
  ISalaryRecruiterEdit,
  SalaryRecruiterFormGroup,
} from 'src/app/interfaces/add-employee.interface';
import {FileManagerService} from 'src/app/services/file-manager/file-manager.service';
import {MAX_FILE_SIZE} from 'src/app/shared/utils/constants';
import {SalaryService} from 'src/app/services/accountant/salary.service';
import {ToastrService} from 'ngx-toastr';
import {debounceTime, distinctUntilChanged, filter, switchMap, tap} from 'rxjs';
import {AccessManagementService} from 'src/app/services/access-management.service';
import {SpinnerOverlayService} from 'src/app/services/spinner.service';
import {EmployeeService} from 'src/app/services/employee.service';
import {IDropdownObject} from 'src/app/interfaces/dropdown-object.interface';

/**
 * Component for recruiter salary tab
 */
@UntilDestroy()
@Component({
  selector: 'app-salary-recruiter',
  templateUrl: './salary-recruiter.component.html',
  styleUrls: ['./salary-recruiter.component.scss'],
})
export class SalaryRecruiterComponent implements OnInit, OnDestroy {
  /**
   * Reactive form for the tab
   */
  public salaryRecruiterForm!: UntypedFormGroup;
  public isEditable = true;
  public ableToEdit = false;
  public formValueDefault!: ISalaryRecruiter;
  public employeeId!: number;
  public isFieldInvalid = Utils.isFieldInvalid;
  public validateAllFormFields = Utils.validateAllFormFields;
  public fileSrc: SafeResourceUrl | string | ArrayBuffer = '';
  public downloadBlobFile = Utils.downloadBlobFile;
  public employmentForm!: IDropdownObject;

  @ViewChild('inputFile') inputFile!: ElementRef;
  @ViewChild('saveButton') saveButton!: ElementRef;

  get ratePerHour(): AbstractControl {
    return this.salaryRecruiterForm.get('ratePerHour')!;
  }

  get salary(): AbstractControl {
    return this.salaryRecruiterForm.get('salary')!;
  }

  get isCreate(): boolean {
    return !this.employeeId;
  }

  get isNotEmployee(): boolean {
    const employmentForm = this.isCreate
      ? this.addEmployeeService.generalData.employmentForm
      : this.employmentForm;

    return (employmentForm &&
      employmentForm.id !== EmploymentFormStatus.Employee) as boolean;
  }

  constructor(
    private route: ActivatedRoute,
    private addEmployeeService: AddEmployeeService,
    private employeeService: EmployeeService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private closeHandler: CloseHandlerService,
    private fileManagerService: FileManagerService,
    private notifier: ToastrService,
    private salaryService: SalaryService,
    private access: AccessManagementService,
    private spinner: SpinnerOverlayService
  ) {}

  ngOnInit(): void {
    this.setEmployeeId();
    this.isEditable = this.isCreate;
    if (!this.isCreate) {
      const accessData = this.route.parent?.snapshot.data['accessData'];
      this.ableToEdit = this.access.salaryRecruiterCreateAccess(accessData);

      if (!this.access.salaryRecruiterReadAccess(accessData)) {
        this.router.navigate(['/profile/', this.employeeId]);
        this.spinner.clearLoadingState();
        return;
      }
    }

    this.salaryRecruiterForm =
      this.buildForm<
        this['isCreate'] extends true ? ISalaryRecruiter : ISalaryRecruiterEdit
      >();
    this.setFormValue();
    this.configureCloseHandler();

    this.subscribeToSalaryChanges();
  }

  setEmployeeId() {
    this.employeeId = +this.route.parent?.snapshot.params['id'];
  }

  setFormValue() {
    if (this.isCreate) {
      this.patchSalaryValue(this.addEmployeeService.salaryData);
      this.setValidation();
    } else {
      this.getTabData();
    }
  }

  patchSalaryValue(value: ISalaryRecruiterEdit | ISalaryRecruiter) {
    this.salaryRecruiterForm.patchValue(value);
    this.formValueDefault = this.salaryRecruiterForm.value;
  }

  getTabData() {
    this.salaryService
      .getEmployeeSalaryForRecruiter(this.employeeId)
      .pipe(
        untilDestroyed(this),
        tap(data => this.patchSalaryValue(data)),
        switchMap(() =>
          this.employeeService.getCurrentEmploymentForm(this.employeeId)
        ),
        tap(data => (this.employmentForm = data))
      )
      .subscribe(() => this.setValidation());
  }

  buildForm<T>(): FormGroup<SalaryRecruiterFormGroup<T>> {
    const commonFields = {
      salary: new FormControl<null | number>(null, [
        Validators.required,
        Validators.max(999999),
      ]),
      ratePerHour: new FormControl<null | number>(null, [
        Validators.required,
        Validators.max(999),
      ]),
      comment: new FormControl<null | string>(null, [
        Validators.maxLength(1000),
      ]),
      dataFile: new FormControl<null | File>(null),
    };

    const editFields = {
      id: new FormControl<number | null>(null),
      date: new FormControl<string | null>(null),
      employeeId: new FormControl<number | null>(null),
    };

    const newFormObject = {
      ...commonFields,
      ...(!this.isCreate && editFields),
    } as SalaryRecruiterFormGroup<T>;

    return new FormGroup(newFormObject) as FormGroup<
      SalaryRecruiterFormGroup<T>
    >;
  }

  subscribeToSalaryChanges() {
    this.salaryRecruiterForm.valueChanges
      .pipe(
        untilDestroyed(this),
        debounceTime(300),
        distinctUntilChanged(),
        filter(() => this.isEditable)
      )
      .subscribe(() => {
        this.closeHandler.notSaved = true;
      });
  }

  configureCloseHandler() {
    this.closeHandler.notSaveForm.pipe(untilDestroyed(this)).subscribe(() => {
      this.salaryRecruiterForm.markAsUntouched();
      this.isEditable = false;
      this.salaryRecruiterForm.patchValue(this.formValueDefault);
    });

    this.closeHandler.clickSavePopup
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.saveButton.nativeElement.click();
      });
  }

  setValidation() {
    if (this.isNotEmployee) {
      this.setValidationForConsultantOrContractor();
    } else {
      this.setValidationForEmployee();
    }
  }

  setValidationForConsultantOrContractor() {
    this.ratePerHour.addValidators(Validators.min(0.1));
    this.salary.setValue(0);
  }

  setValidationForEmployee() {
    this.salary.addValidators(Validators.min(0.1));
    this.ratePerHour.addValidators(Validators.min(0));
    this.ratePerHour.setValue(0);
  }

  /**
   * Method that display icon-pencil on the firts button click
   * If data isn't saved or passed in general, redirect to general tab
   */
  savedData() {
    if (this.salaryRecruiterForm.invalid) {
      this.validateAllFormFields(this.salaryRecruiterForm);
      return;
    }

    this.closeHandler.notSaved = false;
    this.formValueDefault = this.salaryRecruiterForm.value;

    if (this.isCreate) {
      this.saveSalaryCreate();
    } else {
      this.updateEmployeeSalaryForRecruiter();
    }
  }

  saveSalaryCreate() {
    this.addEmployeeService.isSalaryData = true;
    this.addEmployeeService.collectData(this.salaryRecruiterForm.value, true);
    if (this.addEmployeeService.isGeneralData) {
      this.addEmployeeService
        .addEmployee()
        .pipe(untilDestroyed(this))
        .subscribe(id => {
          this.addEmployeeService.cleanVariables();
          this.router.navigate(['/profile/', id]);
        });
    } else {
      this.router.navigate(['profile/adding/general']);
    }
  }

  updateEmployeeSalaryForRecruiter() {
    this.salaryService
      .updateEmployeeSalaryForRecruiter(this.salaryRecruiterForm.getRawValue())
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.isEditable = !this.isEditable;
        this.notifier.success('Saved');
      });
  }

  handleFileInput(event: Event): void {
    const fileToUpload = this.fileManagerService.handleFileInput(
      event,
      [FileTypes.doc, FileTypes.docx, FileTypes.pdf],
      MAX_FILE_SIZE
    );
    if (!fileToUpload) return;

    this.fileSrc = this.sanitizer.bypassSecurityTrustResourceUrl(
      URL.createObjectURL(fileToUpload)
    );
    this.salaryRecruiterForm.controls['dataFile'].setValue(fileToUpload);
  }

  deleteCommentFile(): void {
    this.salaryRecruiterForm.controls['dataFile'].setValue('');
    this.inputFile.nativeElement.value = '';
  }

  switchEdit(event: Event) {
    event.preventDefault();
    this.isEditable = !this.isEditable;
  }

  ngOnDestroy(): void {
    if (this.router.routerState.snapshot.url !== '/profile/adding/general')
      this.addEmployeeService.cleanVariables();
  }
}
